import { AtSign, MessageSquare } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Ícono del canal.
 *
 * Los glifos de marca son SVG propios: `lucide-react` 1.x los sacó de la
 * librería, así que no hay de dónde importarlos. Son trazos simplificados que
 * identifican la red, no reproducciones del logo.
 *
 * Sin color de marca: el ícono hereda el color del texto. Cinco redes con sus
 * cinco colores en una lista de prospectos es exactamente el ruido que el
 * sistema visual saca.
 */
export function ChannelIcon({
  channel,
  className,
}: {
  channel: string;
  className?: string;
}) {
  const clase = cn('h-3.5 w-3.5 shrink-0', className);
  const props = {
    className: clase,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  switch (channel) {
    case 'email':
      return <AtSign className={clase} aria-hidden />;

    case 'whatsapp':
      return (
        <svg {...props}>
          <path d="M3 21l1.6-4.5A8.4 8.4 0 1 1 7.9 20L3 21z" />
          <path d="M8.6 9.2c0 3 2.2 5.2 5.2 5.2l1-1.1 1.6.9-.4 1.3c-2.6.6-6.9-2.1-7.5-6.4l1.3-.5.9 1.6-1 1" />
        </svg>
      );

    case 'linkedin':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="2.5" />
          <path d="M7.5 10.5v6" />
          <circle cx="7.5" cy="7.4" r="0.6" fill="currentColor" stroke="none" />
          <path d="M11.5 16.5v-6M11.5 12.6a2.2 2.2 0 0 1 4.4 0v3.9" />
        </svg>
      );

    case 'instagram':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="3.6" />
          <circle cx="17" cy="7" r="0.7" fill="currentColor" stroke="none" />
        </svg>
      );

    case 'facebook':
      return (
        <svg {...props}>
          <path d="M14.5 21v-8h2.6l.4-3h-3V8.2c0-.9.3-1.5 1.6-1.5H17.6V4a20 20 0 0 0-2.4-.1c-2.4 0-4 1.4-4 4.1V10H8.5v3h2.7v8" />
        </svg>
      );

    default:
      return <MessageSquare className={clase} aria-hidden />;
  }
}

/**
 * El conjunto de canales de un prospecto, en fila.
 *
 * Va como set de íconos y no como cinco chips: con un prospecto tocado por
 * cuatro redes, los chips se comen la fila entera y el nombre —que es lo que
 * el usuario está buscando— queda en segundo plano.
 */
export function ChannelSet({
  channels,
  className,
}: {
  channels: readonly string[];
  className?: string;
}) {
  if (channels.length === 0) return null;
  return (
    <span className={cn('text-fg-muted flex items-center gap-1', className)}>
      {channels.map((c) => (
        <ChannelIcon key={c} channel={c} />
      ))}
    </span>
  );
}
